/**
 * Where `monitoring` deals live.
 *
 * Monitoring means the investor chose to keep following a company they did
 * not invest in this round — the round closed early, diligence ended without
 * a deal, or they never got to assess it in time. Before this surface
 * existed the stage had nowhere to live (`surfacesFor('monitoring')` always
 * said `watchlist`, but nothing rendered it), which is why "Monitor" looked
 * like a working button that quietly did nothing.
 *
 * Layout follows `portfolio.tsx`'s conventions (header block, section divider,
 * empty state) rather than inventing a new shape. Hidden from the tab bar
 * (registered with `href: null` in `_layout.tsx`, same as submissions/help) —
 * it is reached by navigating here, not a seventh tab.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, FlatList } from 'react-native';
import { useRouter } from 'expo-router';
import { Binoculars } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { track, useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { AppLayout } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import { SectionDivider } from '@/components/SectionDivider';
import { SectionHeader } from '@/components/ui';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { useShallow } from 'zustand/react/shallow';
import { appearsOn } from '@/lib/dealflow/stage';
import { DealflowCard } from '@/components/investor/DealflowCard';
import { useComparison, useSavedItems } from '@/lib/state/marketplace';
import { ComparisonBar } from '@/components/investor/DealflowActionBars';
import { useCurrentTier } from '@/lib/state/subscription-store';
import { investorCanSeePlatformDealflow } from '@/lib/subscription/dealflow-gate';
import type { InvestorTier } from '@/lib/subscription/types';
import { useChangeStage } from '@/lib/dealflow/use-change-stage';
import { buildDealStatusConfig } from '@/components/investor/deal-status-badges';
import { openPlans } from '@/lib/commerce/navigation';

const noop = () => {};

export default function WatchlistScreen() {
  useTrackScreen('Watchlist');
  const router = useRouter();
  const colors = useThemeColors();
  // Every card here is 'monitoring'. Shares the pipeline map rather than a
  // one-key local copy, so a stage added to the spine cannot leave this screen
  // rendering an undefined badge.
  const statusConfig = useMemo(() => buildDealStatusConfig(colors), [colors]);

  const dealflow = useDealflowStore(useShallow((s) => s.dealflow));
  const changeStage = useChangeStage();

  // D3 entitlement gate — same rule the score button uses everywhere else
  // (dealflow.tsx, startup-detail.tsx): stays optimistic while loading (P0-4).
  const investorTier = useCurrentTier() as InvestorTier;
  const hasDetailedScoreAccess = investorCanSeePlatformDealflow(investorTier);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const {
    comparisonIds, comparisonCount, addToComparison, removeFromComparison,
    isInComparison, clearComparison, canAddMore,
  } = useComparison(4);
  const { isItemSaved, saveItem, unsaveItem } = useSavedItems();

  const watched = useMemo(
    () => dealflow.filter((d) => appearsOn(d.status, 'watchlist')),
    [dealflow],
  );

  const handleViewStartup = useCallback((dealId: string) => {
    router.push(`/startup-detail?id=${dealId}`);
  }, [router]);

  const handleViewScore = useCallback((dealId: string, e?: { stopPropagation: () => void }) => {
    e?.stopPropagation();
    // Without commerce there is no plans screen to send a locked user to; the
    // score screen itself explains what is locked.
    if (hasDetailedScoreAccess || !openPlans) {
      router.push(`/(investor)/investor-score-detail?dealId=${dealId}`);
    } else {
      openPlans(router, 'investor');
    }
  }, [hasDetailedScoreAccess, router]);

  const handleCompare = useCallback(() => {
    if (comparisonIds.length >= 2) router.push(`/deal-comparison?ids=${comparisonIds.join(',')}`);
  }, [comparisonIds, router]);

  // 'monitoring' never offers 'open-diligence' (see ACTIONS in
  // lib/dealflow/stage.ts) — a watched company that never entered diligence
  // has nothing to open. Defensive lookup only; should not fire.
  const handleDDAction = useCallback((dealId: string) => {
    const deal = watched.find((d) => d.id === dealId);
    if (!deal) return;
    router.push(`/duediligence/${deal.startup.id}`);
  }, [watched, router]);

  /**
   * 'monitoring' offers only 're-engage' and 'view' (ACTIONS in
   * lib/dealflow/stage.ts), so this cannot fire. Requesting access is a move
   * from `interested`; a watched deal must re-engage first, which lands it
   * there and puts the button on the card in the normal place. Required by the
   * prop type — deliberately inert rather than half-wired, because a stage move
   * without the request row strands the deal.
   */
  const handleRequestAccess = useCallback((_dealId: string) => {}, []);

  return (
    <AppLayout title="Watchlist" showSearch={false}>
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Was a title with no way back — the tab bar let you leave but nothing
           said where this sat. Watchlist is where `monitoring` deals live, so
           Dealflow is its parent. */}
        <ScreenFrame
          title="Watchlist"
          back={{ fallback: '/(investor)/dealflow', label: 'Dealflow' }}
        >
          <Animated.View entering={FadeInUp.delay(150).duration(500)} className="px-5 pb-2">
            <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={2}>
              Companies you are following without investing this round. Re-engage when they raise again.
            </Text>
          </Animated.View>

          <View className="px-5">
            <SectionDivider label="Companies" marginTop={12} marginBottom={12} />
          </View>

          {watched.length === 0 ? (
            <View className="flex-1 items-center justify-center px-8">
              <View
                className="w-16 h-16 rounded-full items-center justify-center mb-4"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <Binoculars size={28} color={colors.text.tertiary} />
              </View>
              <Text style={{ color: colors.text.primary }} className="font-semibold text-center mb-1">
                Nothing here yet
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-center text-sm" numberOfLines={3}>
                Choose Monitor on a company to keep following it without investing this round.
              </Text>
            </View>
          ) : (
            <FlatList
              data={watched}
              keyExtractor={(item, i) => `watch-${item.id}-${i}`}
              contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 32, gap: 12 }}
              ListHeaderComponent={
                <SectionHeader
                  title="Monitoring"
                  className="mb-1"
                  right={
                    <Text style={{ color: colors.text.tertiary }} className="text-sm">
                      {watched.length}
                    </Text>
                  }
                />
              }
              renderItem={({ item, index }) => (
                <DealflowCard
                  deal={item}
                  index={index}
                  layout="list"
                  statusConfig={statusConfig}
                  isExpanded={expandedIds.has(item.id)}
                  onToggleExpanded={toggleExpanded}
                  isSelectionMode={false}
                  isSelected={false}
                  onToggleSelection={noop}
                  onEnterSelectionMode={noop}
                  isInComparison={isInComparison(item.id)}
                  canAddMore={canAddMore}
                  onAddToComparison={addToComparison}
                  onRemoveFromComparison={removeFromComparison}
                  isSaved={isItemSaved(item.id)}
                  onSave={saveItem}
                  onUnsave={unsaveItem}
                  onViewStartup={handleViewStartup}
                  onViewScore={handleViewScore}
                  onUpdateStatus={changeStage}
                  onTrack={track}
                  onDDAction={handleDDAction}
                  onRequestAccess={handleRequestAccess}
                />
              )}
            />
          )}

          {comparisonCount > 0 && (
            <ComparisonBar
              comparisonCount={comparisonCount}
              onCompare={handleCompare}
              onClear={clearComparison}
            />
          )}
        </ScreenFrame>
      </View>
    </AppLayout>
  );
}
